import { useCallback, useMemo } from 'react';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';

import { DELIVERY_METHODS, OrderAttrFilter, SEGMENT_STATS } from './orderAttrFilter';

/**
 * 表示対象の localStorage キー。表示項目（DISPLAY_METRICS_STORAGE_KEY）と同じく
 * /map とカード配車で同じ設定を共有する。変えると既存の保存値が読めなくなるので変更しない。
 */
export const ORDER_ATTR_FILTER_STORAGE_KEY = 'convenienceDispatch.orderAttrFilter';

/** localStorage には Set を入れられないので配列で持つ */
type StoredOrderAttrFilter = {
  hiddenDeliveryMethods: string[];
  hiddenSegmentStats: string[];
};

const DEFAULT_STORED: StoredOrderAttrFilter = { hiddenDeliveryMethods: [], hiddenSegmentStats: [] };

const isStringArray = (value: unknown): value is string[] => (
  Array.isArray(value) && value.every((it) => typeof it === 'string')
);

const isStoredOrderAttrFilter = (parsed: unknown): parsed is StoredOrderAttrFilter => {
  if (typeof parsed !== 'object' || parsed === null) return false;
  const candidate = parsed as Partial<StoredOrderAttrFilter>;
  return isStringArray(candidate.hiddenDeliveryMethods) && isStringArray(candidate.hiddenSegmentStats);
};

/** 選択肢に無い値は捨てる。マスタが変わったあとに読めない値が残ると、外した覚えのない案件が消える */
const knownOnly = (values: string[], options: readonly string[]): Set<string> => (
  new Set(values.filter((value) => options.includes(value)))
);

/**
 * 表示対象（配送形態・区間種別）の永続化。
 *
 * 表示項目は localStorage に覚えるのに表示対象だけリロードで戻っていたため、同じ扱いにする
 * （利用者の指摘 2026/08/20）。全部隠した状態も保存する（利用者が意図して外した状態なので、
 * 開き直すたびに戻すと「覚えていない」ことになる）。戻すときは「解除」で全部表示に戻せる。
 */
export function useOrderAttrFilter(): readonly [OrderAttrFilter, (next: OrderAttrFilter) => void] {
  const [stored, setStored] = usePersistedJSON<StoredOrderAttrFilter>(
    ORDER_ATTR_FILTER_STORAGE_KEY,
    DEFAULT_STORED,
    isStoredOrderAttrFilter,
  );

  const filter = useMemo<OrderAttrFilter>(() => ({
    hiddenDeliveryMethods: knownOnly(stored.hiddenDeliveryMethods, DELIVERY_METHODS),
    hiddenSegmentStats: knownOnly(stored.hiddenSegmentStats, SEGMENT_STATS),
  }), [stored]);

  const setFilter = useCallback((next: OrderAttrFilter) => {
    setStored({
      hiddenDeliveryMethods: Array.from(next.hiddenDeliveryMethods),
      hiddenSegmentStats: Array.from(next.hiddenSegmentStats),
    });
  }, [setStored]);

  return [filter, setFilter] as const;
}
